// ============================================================================
// Revenue Trend — Phase 2 parameterized chart widget (2026-06-11).
//
// Ben's concrete ask: "chart the trajectory of income over last month, last
// quarter, last year." This widget reads the SAME revenue source as the
// existing RevenueWidget (mrr_added_by_month, derived from active facilities'
// contract_start) and adds a period selector: Last month / Last quarter /
// Last 12 months. Window total + facility count + delta vs the preceding
// window of equal length, over a bar chart of the window months.
//
// Being a normal registry widget, the Phase 1 tailor/reorder machinery
// applies to it automatically. Default hidden (opt-in via the tailor panel)
// so the default layout stays exactly the pre-personalization dashboard.
// ============================================================================

import { useState } from 'react';
import type { RevenueSnapshot } from './use-dashboard-data';
import {
  WidgetCard,
  WidgetSkeleton,
  fmtCompactCurrency,
} from './widget-card';

type Period = 'month' | 'quarter' | 'year';

const PERIODS: { key: Period; label: string; months: number }[] = [
  { key: 'month', label: '1M', months: 1 },
  { key: 'quarter', label: '3M', months: 3 },
  { key: 'year', label: '12M', months: 12 },
];

const PERIOD_TITLES: Record<Period, string> = {
  month: 'Last month',
  quarter: 'Last quarter',
  year: 'Last 12 months',
};

interface Props {
  data: RevenueSnapshot | null;
  loading: boolean;
}

export function RevenueTrendWidget({ data, loading }: Props) {
  const [period, setPeriod] = useState<Period>('quarter');

  const selector = (
    <div className="flex gap-1" role="tablist" aria-label="Trend period">
      {PERIODS.map((p) => (
        <button
          key={p.key}
          type="button"
          role="tab"
          aria-selected={period === p.key}
          onClick={() => setPeriod(p.key)}
          className={`rounded px-2 py-1 text-xs font-medium tabular-nums ${
            period === p.key
              ? 'bg-tcs-inverse text-tcs-inverse-ink'
              : 'bg-tcs-surface text-tcs-text-muted hover:bg-tcs-surface-strong'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  if (loading && !data) {
    return (
      <WidgetCard title="Revenue trend" accessory={selector} minHeight={240}>
        <WidgetSkeleton lines={4} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Revenue trend" accessory={selector} minHeight={240}>
        <p className="text-sm text-tcs-text-muted">No revenue data available.</p>
      </WidgetCard>
    );
  }

  const months = PERIODS.find((p) => p.key === period)?.months ?? 3;
  const series = data.mrr_added_by_month;
  // Current window = the most recent N months (includes the partial current
  // month). Prior window = the N months immediately before, when available.
  const windowMonths = series.slice(-months);
  const priorMonths = series.slice(
    Math.max(0, series.length - months * 2),
    series.length - months,
  );

  const windowTotal = windowMonths.reduce((s, b) => s + b.mrr_added, 0);
  const windowCount = windowMonths.reduce((s, b) => s + b.count, 0);
  const priorTotal = priorMonths.reduce((s, b) => s + b.mrr_added, 0);
  const hasPrior = priorMonths.length === months;
  const deltaPct =
    hasPrior && priorTotal > 0
      ? ((windowTotal - priorTotal) / priorTotal) * 100
      : null;

  return (
    <WidgetCard
      title="Revenue trend"
      subtitle={`New MRR added · ${PERIOD_TITLES[period].toLowerCase()}`}
      accessory={selector}
      minHeight={240}
    >
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div>
          <div className="text-2xl font-semibold tabular-nums text-tcs-text">
            {fmtCompactCurrency(windowTotal)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
            MRR added · {PERIOD_TITLES[period]}
          </div>
        </div>
        <div>
          <div className="text-lg font-medium tabular-nums text-tcs-text-secondary">
            {windowCount}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
            Facilities onboarded
          </div>
        </div>
        {deltaPct !== null && (
          <div className="ml-auto text-right">
            <div
              className={`text-sm font-medium tabular-nums ${
                deltaPct >= 0 ? 'text-st-ok-fg' : 'text-st-danger-fg'
              }`}
            >
              {deltaPct >= 0 ? '+' : ''}
              {deltaPct.toFixed(0)}%
            </div>
            <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
              vs prior {PERIOD_TITLES[period].toLowerCase().replace('last ', '')}
            </div>
          </div>
        )}
      </div>

      <TrendBars months={windowMonths} />

      <p className="mt-2 text-xs text-tcs-text-muted">
        Source: active facilities' contract_start × monthly_rate — same series
        as the Revenue widget. Current month is partial.
      </p>
    </WidgetCard>
  );
}

// Bar chart over the selected window. Mirrors the RevenueWidget bar style so
// the two read as one family.
function TrendBars({
  months,
}: {
  months: { month: string; mrr_added: number; count: number }[];
}) {
  const HEIGHT = 80;
  const maxAdded = Math.max(1, ...months.map((m) => m.mrr_added));
  return (
    <div className="mt-4">
      <div
        className="flex items-end gap-1"
        style={{ height: HEIGHT }}
        role="img"
        aria-label={`MRR added per month over the selected ${months.length}-month window`}
      >
        {months.map((m, i) => {
          const h = Math.max(2, (m.mrr_added / maxAdded) * HEIGHT);
          const isCurrent = i === months.length - 1;
          return (
            <div
              key={m.month}
              className="flex h-full flex-1 items-end"
              title={`${m.month}: ${fmtCompactCurrency(m.mrr_added)} added across ${m.count} ${m.count === 1 ? 'facility' : 'facilities'}`}
            >
              <div
                className={
                  isCurrent
                    ? 'w-full rounded-sm bg-tcs-inverse'
                    : 'w-full rounded-sm bg-[#5B8DB8]'
                }
                style={{ height: h }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] tabular-nums text-tcs-text-muted">
        <span>{months[0]?.month}</span>
        <span>{months[months.length - 1]?.month}</span>
      </div>
    </div>
  );
}
